import moment from 'moment-timezone'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import SaveAltOutlinedIcon from '@material-ui/icons/SaveAltOutlined'
import DOMPurify from 'dompurify'
import format, { DEFAULT_TIME_ZONE } from 'civic-champs-shared/utils/format'
import { useCurrentDateWithFilename } from 'components/table/ExportCsvMenuItem'
import { TableMenuButton } from 'civic-champs-shared/core'
import { useCurrentOrg } from 'auth/hooks'
import { capitalize, groupBy } from 'lodash'
import useGetBulkPersonNotes from 'note/hooks/useGetBulkPersonNotes'
import { Note } from 'note/Note'

const filename = 'champions.csv'
const baseHeaders = [
  { label: 'First Name', key: 'user.givenName' },
  { label: 'Last Name', key: 'user.familyName' },
  { label: 'Email', key: 'user.email' },
  { label: 'Mobile Phone', key: 'user.phoneNumber' },
  { label: 'Home Phone', key: 'user.homePhoneNumber' },
  { label: 'Contact', key: 'user.expectedContact' },
  { label: 'Hours', key: 'hours' },
  { label: 'Donations', key: 'donations' },
  { label: 'Last Activity', key: 'latestActivity' },
  { label: 'Groups', key: 'groups' },
  { label: 'Address', key: 'user.address' },
  { label: 'Emergency Contact First Name', key: 'user.emergencyContact.givenName' },
  { label: 'Emergency Contact Last Name', key: 'user.emergencyContact.familyName' },
  { label: 'Emergency Contact Relationship', key: 'user.emergencyContactRelationship' },
  { label: 'Emergency Contact Mobile Phone', key: 'user.emergencyContact.phoneNumber' },
  { label: 'Emergency Contact Work Phone', key: 'user.emergencyContact.workPhoneNumber' },
  { label: 'Emergency Contact Home Phone', key: 'user.emergencyContact.homePhoneNumber' },
  { label: 'Emergency Contact Email Address', key: 'user.emergencyContact.email' },
  { label: 'Member Since', key: 'memberSince' },
  { label: 'Date of Birth', key: 'birthDate' },
  { label: 'Tags', key: 'tags' },
  { label: 'Notes', key: 'notes' },
]

const formatPhones = (phones: string[], obj: any) =>
  phones.reduce((acc: any, phone: string) => ({ ...acc, [phone]: format.phoneNumber(obj?.[phone]) }), {})

const formatDate = (value: moment.MomentInput, timeZone: string = DEFAULT_TIME_ZONE) =>
  value == null ? value : moment.tz(value, timeZone).format('MM/DD/YYYY')

const noteToPlainText = (html: string) => {
  const div = document.createElement('div')
  div.innerHTML = DOMPurify.sanitize(html)
  return (div.textContent || div.innerText || '').trim()
}

const formatNoteForExport = (note: Note) => {
  const header = `${format.date(note.createdAt)}${note.title ? ` - ${note.title}` : ''}`
  const body = noteToPlainText(note.body)
  return body ? `${header}: ${body}` : header
}

const formatNotesForExport = (notes: Note[]) => notes.map(formatNoteForExport).join(' | ')

const formatChampionRow = ({ original: { user, volunteering, groups, tags, donation } }: any, timeZone: string) => ({
  user: {
    ...user,
    ...formatPhones(['phoneNumber', 'homePhoneNumber'], user),
    address: format.address(user.address),
    emergencyContact: {
      ...user.emergencyContact,
      ...formatPhones(['phoneNumber', 'homePhoneNumber', 'workPhoneNumber'], user.emergencyContact),
    },
    expectedContact: capitalize(user.expectedContact),
  },
  hours: format.fixedPoint(volunteering.totalVolunteerHours, 1),
  memberSince: formatDate(volunteering.startedAt, timeZone),
  donations: format.fixedPoint(donation.totalDonations / 100, 2),
  latestActivity: formatDate(volunteering.latestActivity, timeZone),
  birthDate: formatDate(user.birthDate, timeZone),
  groups,
  tags: tags.map((tag: any) => tag.name).join(', '),
})

const ExportChampionsButton = ({
  data,
  disabled,
  includeDonations,
  exportLabel = 'Export',
}: {
  data: any[]
  disabled?: boolean
  includeDonations?: boolean | null
  exportLabel?: string
}) => {
  const { timeZone } = useCurrentOrg()
  const [getBulkPersonNotes] = useGetBulkPersonNotes()
  const fileNameWithDate = useCurrentDateWithFilename(filename)
  const csvLink = useRef<CSVLink & HTMLAnchorElement>(null)
  const [preparing, setPreparing] = useState(false)
  const downloadPendingRef = useRef(false)
  const [csvConfig, setCsvConfig] = useState<{ headers: { label: string; key: string }[]; data: any[] }>({
    headers: [],
    data: [],
  })

  useEffect(() => {
    if (!downloadPendingRef.current || csvConfig.data.length === 0) {
      return
    }

    downloadPendingRef.current = false
    // @ts-ignore react-csv exposes the download anchor on .link at runtime
    csvLink.current?.link.click()
  }, [csvConfig])

  const handleExport = useCallback(async () => {
    setPreparing(true)
    try {
      const personIds = data.map(row => row.original.user.id)
      const notes = await getBulkPersonNotes(personIds)
      const notesByPersonId = groupBy(notes, 'entityId')
      const headers = includeDonations ? baseHeaders : baseHeaders.filter(header => header.key !== 'donations')
      const formattedData = data.map(row => ({
        ...formatChampionRow(row, timeZone),
        notes: formatNotesForExport(notesByPersonId[row.original.user.id] || []),
      }))

      downloadPendingRef.current = true
      setCsvConfig({ headers, data: formattedData })
    } finally {
      setPreparing(false)
    }
  }, [data, getBulkPersonNotes, includeDonations, timeZone])

  return (
    <>
      <TableMenuButton
        startIcon={<SaveAltOutlinedIcon />}
        disabled={disabled || data.length === 0 || preparing}
        rightMargin
        onClick={handleExport}
      >
        {exportLabel}
      </TableMenuButton>
      <CSVLink
        style={{ display: 'none' }}
        filename={fileNameWithDate as string}
        headers={csvConfig.headers}
        data={csvConfig.data}
        ref={csvLink}
      />
    </>
  )
}

export default ExportChampionsButton
